import * as RadixPopover from '@radix-ui/react-popover';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentSkillSlashCommandChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/skills/WorkspaceAgentSkillSlashCommandChip';
import { WorkspaceAgentSkillsCatalogListFooter } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/skills/WorkspaceAgentSkillsCatalogListFooter';
import {
  COMPOSER_PICKER_BOTTOM_SIDE_OFFSET_PX,
  type WorkspaceAgentComposerPopoverAnchorRef,
} from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/utils/composerPopoverAnchor';
import {
  workspaceAgentSkillTriggerMenuEmpty,
  workspaceAgentSkillTriggerMenuRowSummary,
  workspaceAgentSkillTriggerMenuSecondary,
  workspaceAgentSkillTriggerMenuTitle,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import {
  WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE,
  WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE,
  WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionRowChrome';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

/** Set as `aria-controls` on the composer's contenteditable while the `/` skill menu is open. */
export const WORKSPACE_AGENT_SKILL_LIST_ID = 'workspace-agent-skill-list';

const WORKSPACE_AGENT_SKILL_OPTION_ID_PREFIX = 'workspace-agent-skill-option-';
const WORKSPACE_AGENT_SKILL_MENU_DESCRIPTION_FIRST_SENTENCE = /^.+?[.](?=\s|$)/;

const workspaceAgentSkillMenuDescriptionLine = (description: string): string => {
  const trimmed = description.trim();
  const match = WORKSPACE_AGENT_SKILL_MENU_DESCRIPTION_FIRST_SENTENCE.exec(trimmed);

  return (match?.[0] ?? trimmed).trim();
};

const formatSkillMenuSelectionAnnouncement = (
  name: string,
  descriptionLine: string | undefined,
  index: number,
  total: number,
): string => [name, descriptionLine, `${index + 1} of ${total}`].filter(Boolean).join(', ');

/** Adjust listbox `scrollTop` only when the option is clipped — avoids `scrollIntoView` repainting before highlight. */
const scrollWorkspaceAgentSkillMenuOptionIntoView = (listbox: HTMLElement, option: HTMLElement): void => {
  const listboxRect = listbox.getBoundingClientRect();
  const optionRect = option.getBoundingClientRect();

  if (optionRect.top < listboxRect.top) {
    listbox.scrollTop -= listboxRect.top - optionRect.top;
  } else if (optionRect.bottom > listboxRect.bottom) {
    listbox.scrollTop += optionRect.bottom - listboxRect.bottom;
  }
};

export type WorkspaceAgentSkillTriggerMenuPopupRef = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export type WorkspaceAgentSkillTriggerMenuSessionProps = {
  anchorRef?: WorkspaceAgentComposerPopoverAnchorRef;
  command: (item: Skill) => void;
  dismissFromPortal: () => void;
  hasSkillsListNextPage: boolean;
  isLoading?: boolean;
  isSkillsListFetchNextPageError: boolean;
  isSkillsListFetchingNextPage: boolean;
  items: readonly Skill[];
  menuListKey: string;
  onActiveDescendantChange?: (optionId: string | undefined) => void;
  requestSkillsListNextPage: () => void;
  retrySkillsListNextPage: () => void;
};

export type WorkspaceAgentSkillTriggerMenuProps = WorkspaceAgentSkillTriggerMenuSessionProps & {
  anchorRef: WorkspaceAgentComposerPopoverAnchorRef;
  side?: 'top' | 'bottom';
  sideOffset?: number;
};

type WorkspaceAgentSkillTriggerMenuRowProps = {
  id: string;
  index: number;
  isKeyboardHighlighted: boolean;
  isSelected: boolean;
  item: Skill;
  onHover: (index: number) => void;
  onSelect: (index: number) => void;
};

const WorkspaceAgentSkillTriggerMenuRow = memo(
  ({
    id,
    index,
    isKeyboardHighlighted,
    isSelected,
    item,
    onHover,
    onSelect,
  }: WorkspaceAgentSkillTriggerMenuRowProps) => {
    const handleMouseEnter = useCallback(() => onHover(index), [index, onHover]);
    const handleMouseUp = useCallback(() => onSelect(index), [index, onSelect]);
    const summary = item.description ? workspaceAgentSkillMenuDescriptionLine(item.description) : '';

    return (
      <button
        aria-selected={isSelected}
        className={cn(
          'flex w-full cursor-pointer flex-col gap-1 rounded-sm p-2 text-left ads:hover:bg-hover-subtle hover:bg-gray-4 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9',
          isSelected && 'ads:bg-hover-subtle bg-gray-4',
        )}
        data-workspace-agent-skill-keyboard-highlight={String(isKeyboardHighlighted)}
        id={id}
        style={
          isKeyboardHighlighted
            ? WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE
            : WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE
        }
        onMouseDown={preserveWorkspaceAgentComposerFocus}
        onMouseEnter={handleMouseEnter}
        onMouseUp={handleMouseUp}
        role="option"
        tabIndex={-1}
        type="button"
      >
        <WorkspaceAgentSkillSlashCommandChip slashCommand={item.slashCommand} />
        {summary ? <span className={workspaceAgentSkillTriggerMenuRowSummary()}>{summary}</span> : null}
      </button>
    );
  },
);

WorkspaceAgentSkillTriggerMenuRow.displayName = 'WorkspaceAgentSkillTriggerMenuRow';

export const WorkspaceAgentSkillTriggerMenu = memo(
  forwardRef<WorkspaceAgentSkillTriggerMenuPopupRef, WorkspaceAgentSkillTriggerMenuProps>(
    (
      {
        anchorRef,
        command,
        dismissFromPortal,
        hasSkillsListNextPage,
        isLoading = false,
        isSkillsListFetchNextPageError,
        isSkillsListFetchingNextPage,
        items,
        onActiveDescendantChange,
        requestSkillsListNextPage,
        retrySkillsListNextPage,
        side = 'top',
        sideOffset = COMPOSER_PICKER_BOTTOM_SIDE_OFFSET_PX,
      },
      ref,
    ) => {
      const listboxRef = useRef<HTMLDivElement>(null);
      const [selectedIndex, setSelectedIndex] = useState(0);
      const [isKeyboardHighlightActive, setIsKeyboardHighlightActive] = useState(false);
      const highlightedIndexRef = useRef(0);
      highlightedIndexRef.current = items.length > 0 ? Math.min(selectedIndex, items.length - 1) : 0;
      const paginationRef = useRef({
        hasSkillsListNextPage,
        isSkillsListFetchNextPageError,
        isSkillsListFetchingNextPage,
        requestSkillsListNextPage,
        retrySkillsListNextPage,
      });
      paginationRef.current = {
        hasSkillsListNextPage,
        isSkillsListFetchNextPageError,
        isSkillsListFetchingNextPage,
        requestSkillsListNextPage,
        retrySkillsListNextPage,
      };

      const activeDescendantId = items[selectedIndex]
        ? `${WORKSPACE_AGENT_SKILL_OPTION_ID_PREFIX}${selectedIndex}`
        : undefined;

      useEffect(() => {
        onActiveDescendantChange?.(activeDescendantId);
      }, [activeDescendantId, onActiveDescendantChange]);

      useIsomorphicLayoutEffect(() => {
        const listbox = listboxRef.current;

        if (!activeDescendantId || !listbox) {
          return;
        }

        const option = listbox.querySelector<HTMLElement>(`#${activeDescendantId}`);

        if (option) {
          scrollWorkspaceAgentSkillMenuOptionIntoView(listbox, option);
        }
      }, [activeDescendantId]);

      const selectItem = useCallback(
        (index: number) => {
          const item = items[index];

          if (item) {
            command(item);
          }
        },
        [command, items],
      );

      const handleHover = useCallback((index: number) => {
        setIsKeyboardHighlightActive(false);
        setSelectedIndex(index);
      }, []);

      const handleListMouseMove = useCallback(() => setIsKeyboardHighlightActive(false), []);

      const handleOpenAutoFocus = useCallback((event: Event) => event.preventDefault(), []);

      const handleEscapeKeyDown = useCallback(
        (event: KeyboardEvent) => {
          dismissFromPortal();
          event.preventDefault();
        },
        [dismissFromPortal],
      );

      const handlePointerDownOutside = useCallback(
        (_event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onPointerDownOutside']>>[0]) => {
          dismissFromPortal();
        },
        [dismissFromPortal],
      );

      useImperativeHandle(
        ref,
        () => ({
          onKeyDown: (event: KeyboardEvent) => {
            if (items.length === 0) {
              return false;
            }

            if (event.key === 'ArrowDown') {
              setIsKeyboardHighlightActive(true);
              const previous = highlightedIndexRef.current;
              const atLastLoadedRow = previous >= items.length - 1;
              const {
                hasSkillsListNextPage: hasNext,
                isSkillsListFetchNextPageError: isFetchError,
                isSkillsListFetchingNextPage: isFetching,
                requestSkillsListNextPage: requestNext,
                retrySkillsListNextPage: retryNext,
              } = paginationRef.current;

              if (atLastLoadedRow && hasNext) {
                if (isFetchError) {
                  retryNext();
                } else if (!isFetching) {
                  requestNext();
                }

                return true;
              }

              setSelectedIndex((previous + 1) % items.length);

              return true;
            }

            if (event.key === 'ArrowUp') {
              setIsKeyboardHighlightActive(true);
              setSelectedIndex((previous) => (previous - 1 + items.length) % items.length);

              return true;
            }

            if (event.key === 'Enter' || event.key === 'Tab') {
              selectItem(highlightedIndexRef.current);

              return true;
            }

            if (event.key === 'Escape') {
              return false;
            }

            return false;
          },
        }),
        [items.length, selectItem],
      );

      const selected = items[selectedIndex];
      const showInitialLoading = isLoading && items.length === 0;
      const showCatalogPageLoading =
        !isLoading &&
        items.length === 0 &&
        !isSkillsListFetchNextPageError &&
        (hasSkillsListNextPage || isSkillsListFetchingNextPage);
      const showEmptyState =
        !isLoading &&
        items.length === 0 &&
        !hasSkillsListNextPage &&
        !isSkillsListFetchingNextPage &&
        !isSkillsListFetchNextPageError;

      const catalogListFooterProps = {
        hasNextPage: hasSkillsListNextPage,
        isFetchNextPageError: isSkillsListFetchNextPageError,
        isFetchingNextPage: isSkillsListFetchingNextPage,
        listRef: listboxRef,
        onRequestNextPage: requestSkillsListNextPage,
        onRetryNextPage: retrySkillsListNextPage,
      };

      return (
        <RadixPopover.Root open>
          <RadixPopover.Anchor virtualRef={anchorRef} />
          <RadixPopover.Portal>
            <RadixPopover.Content
              align="start"
              className="z-portal mb-2 flex max-h-64 min-h-0 w-[var(--radix-popover-trigger-width)] min-w-60 flex-col overflow-hidden rounded-12 border ads:border-default border-gray-4 ads:bg-base bg-gray-1 p-2 ads:text-primary text-gray-12 shadow-popover outline-none"
              collisionPadding={8}
              data-testid="WORKSPACE_AGENT_SKILL_TRIGGER_MENU"
              onEscapeKeyDown={handleEscapeKeyDown}
              onOpenAutoFocus={handleOpenAutoFocus}
              onPointerDownOutside={handlePointerDownOutside}
              // Radix hardcodes `dialog`, which would announce the composer's own popup as a modal.
              role="presentation"
              side={side}
              sideOffset={sideOffset}
            >
              <div className="px-2 pb-2">
                <span className={workspaceAgentSkillTriggerMenuTitle()}>Slash command</span>
              </div>
              <div className="-mx-2 h-px shrink-0 bg-gray-4" />
              {showInitialLoading || showCatalogPageLoading ? (
                <div
                  aria-live="polite"
                  className={cn(
                    'flex shrink-0 items-center justify-center gap-2 px-1.5 py-4',
                    workspaceAgentSkillTriggerMenuSecondary(),
                  )}
                  data-testid="WORKSPACE_AGENT_SKILL_TRIGGER_MENU_STATUS"
                  role="status"
                >
                  <Spinner aria-hidden className="size-4 shrink-0 ads:text-icon-subtle text-gray-9" />
                  Loading skills…
                </div>
              ) : null}
              {showEmptyState ? (
                <p
                  aria-live="polite"
                  className={cn('shrink-0 px-1.5 py-2', workspaceAgentSkillTriggerMenuEmpty())}
                  data-testid="WORKSPACE_AGENT_SKILL_TRIGGER_MENU_STATUS"
                  role="status"
                >
                  No matching skills
                </p>
              ) : null}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-2">
                <div
                  aria-busy={isLoading || isSkillsListFetchingNextPage}
                  aria-label="Skill slash commands"
                  className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto [scroll-behavior:auto]"
                  data-testid="WORKSPACE_AGENT_SKILL_LIST"
                  id={WORKSPACE_AGENT_SKILL_LIST_ID}
                  onMouseMove={handleListMouseMove}
                  ref={listboxRef}
                  role="listbox"
                  style={WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE}
                  tabIndex={-1}
                >
                  {items.map((item, index) => (
                    <WorkspaceAgentSkillTriggerMenuRow
                      id={`${WORKSPACE_AGENT_SKILL_OPTION_ID_PREFIX}${index}`}
                      index={index}
                      isKeyboardHighlighted={isKeyboardHighlightActive && index === selectedIndex}
                      isSelected={index === selectedIndex}
                      item={item}
                      key={item.id}
                      onHover={handleHover}
                      onSelect={selectItem}
                    />
                  ))}
                  <WorkspaceAgentSkillsCatalogListFooter {...catalogListFooterProps} includeStatusUi={false} />
                </div>
                <WorkspaceAgentSkillsCatalogListFooter {...catalogListFooterProps} includeScrollSentinel={false} />
              </div>
              <div aria-live="polite" className="sr-only">
                {selected
                  ? formatSkillMenuSelectionAnnouncement(
                      selected.name,
                      selected.description ? workspaceAgentSkillMenuDescriptionLine(selected.description) : undefined,
                      selectedIndex,
                      items.length,
                    )
                  : null}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      );
    },
  ),
);

WorkspaceAgentSkillTriggerMenu.displayName = 'WorkspaceAgentSkillTriggerMenu';
